/**
 * SafetyMetrics Repository - FERP Repository Pattern Implementation
 *
 * Manages executive safety metrics with proper:
 * - Tenant isolation using findByTenant pattern
 * - Real-time subscription support
 * - Audit trail compliance
 * - Life-critical safety scores from backend calculation
 *
 * ZERO MOCK DATA: All metrics calculated from Redis audit trails
 */

import { gql } from '@ferp/graphql-client';
import { ApolloClient } from '@apollo/client';

/**
 * Domain-specific metric from tenant configuration (DOMAIN-AGNOSTIC)
 * Sprint 1 Phase 3: Updated to match backend DomainMetricOutput schema
 *
 * This type represents a single tenant-configured safety metric.
 * Works for ANY domain: healthcare, manufacturing, financial, legal, education, etc.
 */
export interface DomainMetric {
  /** Domain identifier (e.g., "healthcare", "financial", "legal") */
  domain: string;

  /** Human-readable metric name for UI display (e.g., "Patient Safety Index") */
  name: string;

  /** Current metric value (0.0-100.0) */
  value: number;

  /** Target metric value (0.0-100.0) */
  target: number;

  /** Trend delta from previous period */
  trend: number;

  /** Metric status relative to target */
  status: 'OnTarget' | 'AboveTarget' | 'BelowTarget' | 'AtRisk';
}

/**
 * Executive safety metrics calculated from audit trail data (DOMAIN-AGNOSTIC)
 * Matches ExecutiveSafetyMetricsGql from backend (camelCase)
 *
 * DOMAIN-AGNOSTIC REFACTORING: Added domainMetrics array for dynamic tenant configurations.
 * Old hardcoded fields (patientSafetyIndex, etc.) kept for backward compatibility.
 */
export interface ExecutiveSafetyMetrics {
  /** Overall safety score (0-100) - weighted average of all domain metrics */
  overallSafetyScore: number;

  /** Domain-specific metrics loaded from tenant configuration (DOMAIN-AGNOSTIC)
   *
   * This array replaces hardcoded domain-specific fields.
   * Frontend should iterate over this array to display metrics dynamically.
   *
   * Examples:
   * - Healthcare: patient_safety, phi_protection, medication_accuracy
   * - Manufacturing: equipment_safety, worker_safety, quality_control
   * - Financial: fraud_prevention, transaction_security, regulatory_compliance
   */
  domainMetrics: DomainMetric[];

  /** @deprecated Use domainMetrics array instead - Patient safety index (0-100) - healthcare-specific safety events */
  patientSafetyIndex: number;

  /** @deprecated Use domainMetrics array instead - Financial fraud prevention score (0-100) - financial integrity */
  financialFraudPrevention: number;

  /** @deprecated Use domainMetrics array instead - Legal privilege protection score (0-100) - legal compliance */
  legalPrivilegeProtection: number;

  /** Regulatory compliance score (0-100) - framework adherence */
  regulatoryCompliance: number;

  /** Average emergency response time in milliseconds */
  emergencyResponseTime: number;

  /** Audit trail integrity score (0-100) - cryptographic verification */
  auditTrailIntegrity: number;

  /** Unix timestamp in milliseconds when metrics were last calculated */
  lastUpdated: number;

  /** Trend direction based on historical comparison */
  trend: 'IMPROVING' | 'STABLE' | 'DECLINING';

  /** Risk level based on overall score thresholds */
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
}

/**
 * Input for querying executive safety metrics
 * Must match backend SafetyMetricsInput exactly
 */
export interface SafetyMetricsInput {
  /** Tenant ID for metrics calculation */
  tenant: string;

  /** Time window for metric calculation in seconds (default: 86400 = 24 hours) */
  timeWindowSecs?: number;
}

/**
 * GraphQL query for executive safety metrics (DOMAIN-AGNOSTIC)
 * Replaces hardcoded mock data with real audit trail calculations
 *
 * UPDATED: Added domainMetrics array for tenant-specific dynamic metrics
 */
const EXECUTIVE_SAFETY_METRICS_QUERY = gql`
  query ExecutiveSafetyMetrics($input: SafetyMetricsInput!) {
    executiveSafetyMetrics(input: $input) {
      overallSafetyScore
      domainMetrics {
        domain
        name
        value
        target
        trend
        status
      }
      patientSafetyIndex
      financialFraudPrevention
      legalPrivilegeProtection
      regulatoryCompliance
      emergencyResponseTime
      auditTrailIntegrity
      lastUpdated
      trend
      riskLevel
    }
  }
`;

/**
 * GraphQL subscription for real-time safety metrics updates (DOMAIN-AGNOSTIC)
 * Sprint 1 Phase 3: Matches backend safetyMetricsUpdates subscription
 * Updates every 30 seconds (backend interval)
 *
 * UPDATED: Added domainMetrics array for tenant-specific dynamic metrics
 * FIXED: Changed from safetyMetricsUpdated → safetyMetricsUpdates (matches Phase 3 backend)
 * FIXED: Input parameter now uses SafetyMetricsInput! type (matches backend schema)
 */
const SAFETY_METRICS_SUBSCRIPTION = gql`
  subscription SafetyMetricsUpdates($input: SafetyMetricsInput!) {
    safetyMetricsUpdates(input: $input) {
      overallSafetyScore
      domainMetrics {
        domain
        name
        value
        target
        trend
        status
      }
      patientSafetyIndex
      financialFraudPrevention
      legalPrivilegeProtection
      regulatoryCompliance
      emergencyResponseTime
      auditTrailIntegrity
      lastUpdated
      trend
      riskLevel
    }
  }
`;

/**
 * Repository for executive safety metrics (CEO Dashboard)
 * FERP Pattern: Tenant-isolated data access with real-time updates
 */
export class SafetyMetricsRepository {
  /**
   * Fetch executive safety metrics for CEO dashboard
   * LIFE-CRITICAL: Real-time safety scores from audit trail analysis
   *
   * @param client - Apollo Client instance (must be passed from React component using useApolloClient hook)
   * @param tenantId - Tenant ID for metrics calculation
   * @param timeWindowSecs - Time window in seconds (default: 86400 = 24 hours)
   * @returns Executive safety metrics calculated from audit trails
   */
  static async findByTenant(
    client: ApolloClient,
    tenantId: string,
    timeWindowSecs: number = 86400
  ): Promise<ExecutiveSafetyMetrics> {
    const { data, error } = await client.query<Record<string, any>>({
      query: EXECUTIVE_SAFETY_METRICS_QUERY,
      variables: {
        input: {
          tenant: tenantId,
          timeWindowSecs,
        },
      },
      // Force network fetch for life-critical data (no cache)
    });

    if (error) {
      throw new Error(`Failed to fetch safety metrics: ${error.message}`);
    }

    if (!data?.executiveSafetyMetrics) {
      throw new Error('No safety metrics data returned from backend');
    }

    return data?.executiveSafetyMetrics as ExecutiveSafetyMetrics;
  }

  /**
   * Subscribe to real-time safety metrics updates
   * Sprint 1 Phase 3: Real-time subscription using safetyMetricsUpdates
   * Updates every 30 seconds (backend interval)
   *
   * @param client - Apollo Client instance (must be passed from React component using useApolloClient hook)
   * @param tenantId - Tenant ID for metrics calculation
   * @param onUpdate - Callback for metrics updates
   * @param timeWindowSecs - Time window in seconds (default: 86400 = 24 hours)
   * @returns Subscription cleanup function
   */
  static subscribeToUpdates(
    client: ApolloClient,
    tenantId: string,
    onUpdate: (metrics: ExecutiveSafetyMetrics) => void,
    timeWindowSecs: number = 86400
  ): () => void {
    const subscription = client.subscribe({
      query: SAFETY_METRICS_SUBSCRIPTION,
      variables: {
        input: {
          tenant: tenantId,
          timeWindowSecs,
          // includeDeprecated: true by default in backend
        },
      },
    });

    const observer = subscription.subscribe({
      next: ({ data }: { data: any }) => {
        if (data?.safetyMetricsUpdates) {
          onUpdate(data?.safetyMetricsUpdates as ExecutiveSafetyMetrics);
        }
      },
      error: (error: Error) => {
        console.error('Safety metrics subscription error:', error);
      },
    });

    // Return cleanup function
    return () => {
      observer.unsubscribe();
    };
  }

  /**
   * Get risk level color for UI display (data-driven from backend)
   * Maps backend risk levels to Mantine theme colors
   */
  static getRiskLevelColor(riskLevel: string): string {
    switch (riskLevel) {
      case 'LOW':
        return 'green';
      case 'MEDIUM':
        return 'yellow';
      case 'HIGH':
        return 'orange';
      case 'CRITICAL':
        return 'red';
      default:
        return 'gray';
    }
  }

  /**
   * Get trend icon for UI display (data-driven from backend)
   * Maps backend trend values to Tabler icon names
   */
  static getTrendIcon(trend: string): string {
    switch (trend) {
      case 'IMPROVING':
        return 'trending-up';
      case 'STABLE':
        return 'trending-flat';
      case 'DECLINING':
        return 'trending-down';
      default:
        return 'trending-flat';
    }
  }

  /**
   * Format emergency response time for display
   */
  static formatEmergencyResponseTime(milliseconds: number): string {
    if (milliseconds < 1000) {
      return `${milliseconds}ms`;
    } else if (milliseconds < 60000) {
      return `${(milliseconds / 1000).toFixed(1)}s`;
    } else {
      return `${(milliseconds / 60000).toFixed(1)}m`;
    }
  }
}
